import * as THREE from 'three'
import hal3 from './musics/hal3.ogg'
import phonk1 from './musics/phonk1.ogg'
import { BlockType } from '../terrain'

export type MusicStyle = 'default' | 'phonk'

const trackUrls: Record<MusicStyle, string> = {
  default: hal3,
  phonk: phonk1
}

import grass1 from './blocks/grass1.ogg'
import grass2 from './blocks/grass2.ogg'
import grass3 from './blocks/grass3.ogg'
import grass4 from './blocks/grass4.ogg'

import sand1 from './blocks/sand1.ogg'
import sand2 from './blocks/sand2.ogg'
import sand3 from './blocks/sand3.ogg'
import sand4 from './blocks/sand4.ogg'

import stone1 from './blocks/stone1.ogg'
import stone2 from './blocks/stone2.ogg'
import stone3 from './blocks/stone3.ogg'
import stone4 from './blocks/stone4.ogg'

import dirt1 from './blocks/dirt1.ogg'
import dirt2 from './blocks/dirt2.ogg'
import dirt3 from './blocks/dirt3.ogg'
import dirt4 from './blocks/dirt4.ogg'

import tree1 from './blocks/tree1.ogg'
import tree2 from './blocks/tree2.ogg'
import tree3 from './blocks/tree3.ogg'
import tree4 from './blocks/tree4.ogg'

import leaf1 from './blocks/leaf1.ogg'
import leaf2 from './blocks/leaf2.ogg'
import leaf3 from './blocks/leaf3.ogg'
import leaf4 from './blocks/leaf4.ogg'
import { isMobile } from '../utils'

export default class Audio {
  constructor(camera: THREE.PerspectiveCamera) {
    if (isMobile) return

    const listener = new THREE.AudioListener()
    this.audioLoader = new THREE.AudioLoader()
    camera.add(listener)

    // restore persisted music style
    const savedStyle = window.localStorage.getItem('musicStyle')
    if (savedStyle === 'phonk' || savedStyle === 'default') {
      this.musicStyle = savedStyle
    }

    // load bgm
    this.bgm = new THREE.Audio(listener)
    this.bgm.autoplay = false
    this.loadBgm(this.musicStyle)

    // play / pause bgm
    document.addEventListener('pointerlockchange', () => {
      if (!this.bgm) return
      if (
        document.pointerLockElement &&
        !this.bgm.isPlaying &&
        !this.disabled
      ) {
        this.bgm.play()
      } else {
        this.bgm.pause()
      }
    })

    // load sound effect
    for (const types of this.sourceSet) {
      const audios: THREE.Audio[] = []
      for (const type of types) {
        this.audioLoader.load(type, buffer => {
          const audio = new THREE.Audio(listener!)
          audio.setBuffer(buffer)
          audio.setVolume(0.15)
          audios.push(audio)
        })
      }
      this.soundSet.push(audios)
    }
  }

  bgm?: THREE.Audio
  audioLoader?: THREE.AudioLoader
  musicStyle: MusicStyle = 'default'

  loadBgm = (style: MusicStyle) => {
    if (!this.bgm || !this.audioLoader) return
    if (this.bgm.isPlaying) this.bgm.stop()
    this.audioLoader.load(trackUrls[style], buffer => {
      if (!this.bgm) return
      this.bgm.setBuffer(buffer)
      this.bgm.setVolume(0.1)
      this.bgm.setLoop(true)
      if (
        document.pointerLockElement &&
        !this.disabled &&
        !this.bgm.isPlaying
      ) {
        this.bgm.play()
      }
    })
  }

  setMusicStyle = (style: MusicStyle) => {
    if (style === this.musicStyle) return
    this.musicStyle = style
    window.localStorage.setItem('musicStyle', style)
    this.loadBgm(style)
  }

  disabled = false

  sourceSet = [
    [grass1, grass2, grass3, grass4],
    [sand1, sand2, sand3, sand4],
    [tree1, tree2, tree3, tree4],
    [leaf1, leaf2, leaf3, leaf4],
    [dirt1, dirt2, dirt3, dirt4],
    [stone1, stone2, stone3, stone4],
    [stone1, stone2, stone3, stone4],
    [tree1, tree2, tree3, tree4],
    [stone1, stone2, stone3, stone4],
    [stone1, stone2, stone3, stone4],
    [stone1, stone2, stone3, stone4],
    [stone1, stone2, stone3, stone4],
    [tree1, tree2, tree3, tree4],
    [sand1, sand2, sand3, sand4],
    [sand1, sand2, sand3, sand4],
    [stone1, stone2, stone3, stone4],
    [stone1, stone2, stone3, stone4],
    [grass1, grass2, grass3, grass4],
    [grass1, grass2, grass3, grass4], // 18: whitespaceGrass
    [dirt1, dirt2, dirt3, dirt4],     // 19: whitespaceDirt
    [tree1, tree2, tree3, tree4],     // 20: whitespaceLog
    [leaf1, leaf2, leaf3, leaf4],     // 21: whitespaceLeaf
    [stone1, stone2, stone3, stone4], // 22: netherrack
    [sand1, sand2, sand3, sand4],     // 23: soulSand
    [stone1, stone2, stone3, stone4], // 24: glowstone
    [stone1, stone2, stone3, stone4], // 25: netherQuartz
    [leaf1, leaf2, leaf3, leaf4]      // 26: netherPortal
  ]

  soundSet: THREE.Audio[][] = []

  index = 0

  playSound(type: BlockType) {
    if (!this.disabled && !isMobile) {
      this.index++ === 3 && (this.index = 0)
      this.soundSet[type]?.[this.index]?.play()
    }
  }

  playPortalSound() {
    if (typeof window === 'undefined') return
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioContext) return
    const ctx = new AudioContext()

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    const filter = ctx.createBiquadFilter()

    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(80, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.8)
    osc.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 1.6)

    filter.type = 'peaking'
    filter.Q.value = 10
    filter.frequency.setValueAtTime(100, ctx.currentTime)
    filter.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 1.2)

    gain.gain.setValueAtTime(0.001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.4)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.6)

    osc.connect(filter)
    filter.connect(gain)
    gain.connect(ctx.destination)

    osc.start()
    osc.stop(ctx.currentTime + 1.7)
  }
}
